import type { SelectorOption } from "~/ui/selectors/Selector";

// Languages from Assembly's supported languages: https://www.assemblyai.com/docs/speech-to-text/supported-languages
// IMPORTANT: only left -> right languages are NOT supported officially in the app right now. RtL Languages, such as
// Arabic (ar), Hebrew (he), Pashto (ps), Persian (fa), Sindhi (sd), Urdu (ur), Yiddish (yi) can be used in the recap and
// transcription, but we don't support them officially since we don't handle all cases with this content properly in the UI yet.
// keep in sync with values in apps/meeting_recorder/api/meeting_summarizer/transcription_providers.py
export const languageCodeToNameMap = {
	// Languages supported by assembly's 'best' model
	/* eslint-disable camelcase */
	en: { native: "English", english: "English" },
	en_uk: { native: "English (U.K.)", english: "English (U.K.)" },
	en_au: { native: "English (Australia)", english: "English (Australia)" },
	en_us: { native: "English (U.S.A.)", english: "English (U.S.A.)" },
	/* eslint-enable camelcase */
	es: { native: "Español", english: "Spanish" },
	fr: { native: "Français", english: "French" },
	de: { native: "Deutsch", english: "German" },
	it: { native: "Italiano", english: "Italian" },
	pt: { native: "Português", english: "Portuguese" },
	nl: { native: "Nederlands", english: "Dutch" },
	ja: { native: "日本語", english: "Japanese" },
	zh: { native: "中文", english: "Chinese" },
	fi: { native: "Suomi", english: "Finnish" },
	ko: { native: "한국어", english: "Korean" },
	pl: { native: "Polski", english: "Polish" },
	ru: { native: "Русский", english: "Russian" },
	tr: { native: "Türkçe", english: "Turkish" },
	uk: { native: "Українська", english: "Ukrainian" },
	vi: { native: "Tiếng Việt", english: "Vietnamese" },
	// Languages supported by assembly's 'nano' model
	af: { native: "Afrikaans", english: "Afrikaans" },
	sq: { native: "Shqip", english: "Albanian" },
	am: { native: "አማርኛ", english: "Amharic" },
	ar: { native: "العربية", english: "Arabic" },
	hy: { native: "Հայերեն", english: "Armenian" },
	as: { native: "অসমীয়া", english: "Assamese" },
	az: { native: "Azərbaycan", english: "Azerbaijani" },
	ba: { native: "Bashkir", english: "Bashkir" },
	eu: { native: "Euskara", english: "Basque" },
	be: { native: "Беларуская", english: "Belarusian" },
	bn: { native: "বাংলা", english: "Bengali" },
	bs: { native: "Bosanski", english: "Bosnian" },
	br: { native: "Brezhoneg", english: "Breton" },
	bg: { native: "Български", english: "Bulgarian" },
	my: { native: "မြန်မာ", english: "Burmese" },
	ca: { native: "Català", english: "Catalan" },
	hr: { native: "Hrvatski", english: "Croatian" },
	cs: { native: "Čeština", english: "Czech" },
	da: { native: "Dansk", english: "Danish" },
	et: { native: "Eesti", english: "Estonian" },
	fo: { native: "Føroyskt", english: "Faroese" },
	gl: { native: "Galego", english: "Galician" },
	ka: { native: "Ქართული", english: "Georgian" },
	el: { native: "Ελληνικά", english: "Greek" },
	gu: { native: "ગુજરાતી", english: "Gujarati" },
	ht: { native: "Haitian Creole", english: "Haitian" },
	ha: { native: "Hausa", english: "Hausa" },
	haw: { native: "ʻŌlelo Hawaiʻi", english: "Hawaiian" },
	he: { native: "עברית", english: "Hebrew" },
	hi: { native: "हिन्दी", english: "Hindi" },
	hu: { native: "Magyar", english: "Hungarian" },
	is: { native: "Íslenska", english: "Icelandic" },
	id: { native: "Indonesia", english: "Indonesian" },
	jv: { native: "Jawa", english: "Javanese" },
	kn: { native: "ಕನ್ನಡ", english: "Kannada" },
	kk: { native: "Қазақ тілі", english: "Kazakh" },
	km: { native: "ខ្មែរ", english: "Khmer" },
	lo: { native: "ລາວ", english: "Lao" },
	la: { native: "Latin", english: "Latin" },
	lv: { native: "Latviešu", english: "Latvian" },
	ln: { native: "Lingála", english: "Lingala" },
	lt: { native: "Lietuvių", english: "Lithuanian" },
	lb: { native: "Lëtzebuergesch", english: "Luxembourgish" },
	mk: { native: "Македонски", english: "Macedonian" },
	mg: { native: "Malagasy", english: "Malagasy" },
	ms: { native: "Melayu", english: "Malay" },
	ml: { native: "മലയാളം", english: "Malayalam" },
	mt: { native: "Malti", english: "Maltese" },
	mi: { native: "Māori", english: "Maori" },
	mr: { native: "मराठी", english: "Marathi" },
	mn: { native: "Монгол", english: "Mongolian" },
	ne: { native: "नेपाली", english: "Nepali" },
	no: { native: "Norsk", english: "Norwegian" },
	nn: { native: "Norsk nynorsk", english: "Norwegian Nynorsk" },
	oc: { native: "Occitan", english: "Occitan" },
	pa: { native: "ਪੰਜਾਬੀ", english: "Panjabi" },
	ps: { native: "پښتو", english: "Pashto" },
	fa: { native: "فارسی", english: "Persian" },
	ro: { native: "Română", english: "Romanian" },
	sa: { native: "संस्कृत भाषा", english: "Sanskrit" },
	sr: { native: "Српски", english: "Serbian" },
	sn: { native: "ChiShona", english: "Shona" },
	sd: { native: "سنڌي", english: "Sindhi" },
	si: { native: "සිංහල", english: "Sinhala" },
	sk: { native: "Slovenčina", english: "Slovak" },
	sl: { native: "Slovenščina", english: "Slovenian" },
	so: { native: "Soomaali", english: "Somali" },
	su: { native: "Basa Sunda", english: "Sundanese" },
	sw: { native: "Kiswahili", english: "Swahili" },
	sv: { native: "Svenska", english: "Swedish" },
	tl: { native: "Filipino", english: "Tagalog" },
	tg: { native: "Тоҷикӣ", english: "Tajik" },
	ta: { native: "தமிழ்", english: "Tamil" },
	tt: { native: "Татар", english: "Tatar" },
	te: { native: "తెలుగు", english: "Telugu" },
	th: { native: "ไทย", english: "Thai" },
	bo: { native: "བོད་སྐད་", english: "Tibetan" },
	tk: { native: "Türkmen dili", english: "Turkmen" },
	ur: { native: "اردو", english: "Urdu" },
	uz: { native: "O'zbek", english: "Uzbek" },
	cy: { native: "Cymraeg", english: "Welsh" },
	yi: { native: "יִדיש", english: "Yiddish" },
	yo: { native: "Èdè Yorùbá", english: "Yoruba" },
} as const;

export type TranscriptLanguageCodeType = keyof typeof languageCodeToNameMap;
export type TranscriptLanguageEnglishNameType = (typeof languageCodeToNameMap)[TranscriptLanguageCodeType]["english"];
export type TranscriptLanguageNativeNameType = (typeof languageCodeToNameMap)[TranscriptLanguageCodeType]["native"];

export const supportedTranscriptLanguageCodes = Object.keys(languageCodeToNameMap) as TranscriptLanguageCodeType[];

export interface TranscriptLanguageDescriptor {
	code: TranscriptLanguageCodeType;
	englishName: TranscriptLanguageEnglishNameType;
	nativeName: TranscriptLanguageNativeNameType;
}

export function getLanguageInformationForCode(code: TranscriptLanguageCodeType): TranscriptLanguageDescriptor {
	return {
		code: code,
		englishName: languageCodeToNameMap[code]["english"],
		nativeName: languageCodeToNameMap[code]["native"],
	};
}

export function languageCodeIsSupported(code: string): code is TranscriptLanguageCodeType {
	return supportedTranscriptLanguageCodes.includes(code as TranscriptLanguageCodeType);
}

export type TranscriptLanguageOption = SelectorOption & {
	value: TranscriptLanguageCodeType;
};

export function languageIsEnglish(code: TranscriptLanguageCodeType): boolean {
	return code.startsWith("en");
}

export function getAllLanguageOptionsAvailableForTranscription(): TranscriptLanguageOption[] {
	return supportedTranscriptLanguageCodes.map(code => {
		const languageValues = getLanguageInformationForCode(code);
		if (languageIsEnglish(code)) {
			return { label: languageValues.englishName, value: code };
		}
		return { label: `${languageValues.englishName} (${languageValues.nativeName})`, value: code };
	});
}
